import { useEffect, useRef } from 'react';

import { resetMatomoUser, setMatomoUser } from '@utils/matomo';

import useAccount from './useAccount';

/**
 * Keeps the Matomo user context in sync with authentication.
 *
 * - When a user is (or becomes) logged in, sends their id, a readable label and
 *   role to Matomo.
 * - When they log out (account cleared after having been set), resets the user
 *   context and starts a fresh visit.
 *
 * All Matomo calls are no-ops when analytics is disabled, so this hook is safe
 * to mount unconditionally.
 */
const useMatomoUser = (): void => {
  const { account } = useAccount();
  const trackedId = useRef<string | null>(null);

  useEffect(() => {
    if (account?.id) {
      setMatomoUser({
        id: account.id,
        name: account.fullName
          ? `${account.fullName} (${account.email})`
          : account.email,
        role: account.adminRole,
      });
      trackedId.current = account.id;
    } else if (trackedId.current) {
      // Was logged in, now logged out.
      resetMatomoUser();
      trackedId.current = null;
    }
  }, [account?.id, account?.fullName, account?.email, account?.adminRole]);
};

export default useMatomoUser;
